import type { FaultConfig, KpiConfig } from '../../engine/plant/types';

export const faults: FaultConfig[] = [
  {
    id: 'CW-SUMMER',
    name: 'Summer cooling-water temperature rise',
    kind: 'sourceRamp',
    target: 'SRC-CW',
    field: 'temperature',
    params: { delta: 16, overS: 5400 },
    description:
      'Cooling-water supply climbs 16 K over ninety minutes — a still, hot afternoon on an air-cooled circuit. The jacket runs out of driving ' +
      'force long before the end of that, and the reactor will not hold 350 K on cooling alone.',
  },
  { id: 'CWP-TRIP', name: 'Cooling-water pump trip', kind: 'pumpTrip', target: 'CWP-201', description: 'The jacket cooling-water pump stops. The reactor has minutes, not hours.' },
  {
    id: 'FEED-A-STEP',
    name: 'Feed A concentration step',
    kind: 'sourceRamp',
    target: 'SRC-A',
    field: 'A',
    params: { delta: 0.45, overS: 300 },
    description: 'A new batch of feed arrives 30 % stronger — more heat release at the same flow.',
  },
  {
    id: 'FEED-A-WEAK',
    name: 'Feed A concentration drop',
    kind: 'sourceRamp',
    target: 'SRC-A',
    field: 'A',
    params: { delta: -0.4, overS: 600 },
    description: 'Feed A runs weak. Conversion holds but throughput of product falls.',
  },
  { id: 'AGITATOR-FAIL', name: 'Agitator failure', kind: 'agitatorFail', target: 'R-201', description: 'The agitator seizes. Jacket UA fades to 35 % over the next few minutes.' },
  { id: 'CATALYST-UP', name: 'Catalyst activity increase', kind: 'catalystChange', target: 'R-201', params: { factor: 1.7 }, description: 'A fresh catalyst charge is 70 % more active. Same feed, much more heat.' },
  { id: 'CATALYST-DOWN', name: 'Catalyst deactivation', kind: 'catalystChange', target: 'R-201', params: { factor: 0.55 }, description: 'The catalyst has aged. Conversion falls and unreacted A carries over.' },
  { id: 'JACKET-FOUL', name: 'Jacket fouling', kind: 'fouling', target: 'R-201', params: { factor: 0.6 }, description: 'Scale on the jacket wall cuts UA by 40 %.' },
  { id: 'FV202-STICK', name: 'Jacket valve stiction', kind: 'valveStiction', target: 'FV-202', params: { S: 5, J: 2.5 }, description: 'The cooling-water valve sticks and slips, cycling the reactor temperature.' },
  { id: 'FV202-STUCK', name: 'Jacket valve stuck', kind: 'valveStuck', target: 'FV-202', params: { position: 0.35 }, description: 'The cooling-water valve jams part open and stops responding.' },
  { id: 'TT201-FREEZE', name: 'Reactor thermocouple frozen', kind: 'txFreeze', target: 'TT-201', description: 'The reactor temperature transmitter holds its last reading. The controller stops seeing the truth.' },
  { id: 'TT201-DRIFT', name: 'Reactor thermocouple drifting low', kind: 'txDrift', target: 'TT-201', params: { pctPerHour: 5, sign: -1 }, description: 'The thermocouple reads progressively low. The controller under-cools.' },
  { id: 'PT301-SPIKE', name: 'Separator pressure transmitter spiking', kind: 'txSpike', target: 'PT-301', params: { perHour: 25, pctSpan: 20 }, description: 'Intermittent spikes on the separator pressure signal.' },
  { id: 'AIR-LOSS', name: 'Instrument air failure', kind: 'airLoss', target: '*', description: 'Every valve strokes to its fail position: feeds shut, cooling wide open, vent wide open.' },
];

export const kpis: KpiConfig = {
  spec: [
    { tag: 'AT-201.PV', hi: 0.25, label: 'Unreacted A ≤ 0.25 kmol/m³' },
    { tag: 'TT-201.PV', lo: 340, hi: 358, label: 'Reactor 340–358 K' },
    { tag: 'PT-301.PV', lo: 1.5, hi: 2.8, label: 'Separator 1.5–2.8 bara' },
    { tag: 'LT-203.PV', lo: 45, hi: 95, label: 'Reactor level 45–95 %' },
  ],
  throughput: { tag: 'S-302.Q', label: 'Product made', units: 'm³' },
  energy: [
    { tag: 'R-201.QJKT', label: 'Jacket duty', units: 'kW' },
    { tag: 'R-201.QRXN', label: 'Reaction heat', units: 'kW' },
    { tag: 'HX-103.DUTY', label: 'Preheat duty', units: 'kW' },
  ],
  chemicals: [{ tag: 'R-201.CONV', label: 'Conversion', units: '%' }],
  danger: [
    { id: 'temp', label: 'Reactor above 365 K', cond: { op: '>', tag: 'TT-201.PV', value: 365 } },
    { id: 'roc', label: 'Reactor temperature rising fast', cond: { op: 'rate', tag: 'TT-201.PV', overS: 60, gt: 0.02 } },
    { id: 'press', label: 'Separator above 2.9 bara', cond: { op: '>', tag: 'PT-301.PV', value: 2.9 } },
    { id: 'nocool', label: 'Jacket cooling lost', cond: { op: '<', tag: 'FT-202.PV', value: 4 } },
  ],
  loops: ['FIC-101', 'FFIC-102', 'TIC-103', 'TIC-201', 'FIC-202', 'LIC-203', 'PIC-301', 'LIC-302'],
};
